import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import WidgetView from 'scoreboard/dashboards/widgets/widget-view'
import template from './chart-widget-view.hbs'
import WidgetEmptyState from '../widget-empty-state'
import renderChartDesigner from 'scoreboard/charts/render-chart-designer'
import serieDrilldown from 'scoreboard/charts/serie-drilldown'
import ChartWidgetConfigMenu from 'scoreboard/dashboards/widgets/chart/config/chart-widget-config-menu'
import unwrapModel from 'scoreboard/components/unwrap-model'
import loadingDots from 'scoreboard/components/loading-dots.hbs'
import calendars from 'scoreboard/components/calendars'
import ColorLegend from 'scoreboard/charts/color-legend'
import shouldRenderLegend, {
	CHART_TITLE
} from 'scoreboard/charts/should-render-legend'
import DrilldownManager, { DrilldownState } from 'scoreboard/charts/drilldown-manager'
import constants from 'scoreboard/components/constants'
const { BORDER_WIDTH } = constants
import getZoomToIds from 'scoreboard/charts/get-zoom-to-ids'
import getChartAccessoryMetadata from './get-chart-accessory-metadata'

// Make the chart and its accessories as close as possible without their marquees
// overlapping. That's 10px (marquee padding) + 1px (marquee border)
// times 2 for the bottom of the chart wob and the top of the table wob
const WIDGET_MARGIN = (10 + BORDER_WIDTH) * 2

// height/width settings for legend widget, varies depending on the widget type
const LEGEND_WIDGET_VERTICAL_WIDTH = 200
const LEGEND_WIDGET_HORIZONTAL_HEIGHT = 50

// the accessory types for the chart, widgets that should appear with the chart
// widget
const CHART_ACCESSORY_TYPES = ['chart-table', 'chart-color-legend']

const ChartWidgetView = WidgetView.extend({
	className: 'widget chart',

	nonFetchProperties: WidgetView.prototype.nonFetchProperties.concat(['label', 'showPeriodLabel',
		// Prevent unnecessary fetch after adding "existing chart" widget
		'chartId', 'chartTempId']),

	initialize: function (opt) {
		WidgetView.prototype.initialize.apply(this, arguments)

		this.listenTo(this.model.wob, 'change:selected', () => {
			const avs = this.getAccessoryViews()
			this.model.wob.accessoryWobs = _.map(avs, av => av.model.wob)
		})

		this.listenTo(this.model.chart, 'change', e => {
			// update any accessories that may have changed
			_.each(CHART_ACCESSORY_TYPES, type => this.toggleChartAccessory(type))

			// If the only change to the model is that the data table was
			// toggled on/off, that shouldn't affect the chart so no need to
			// re-render it.
			// Note that there is code elsewhere to handle the color legend accessory
			if (singlePropChanged(e, 'chartTable')) return

			// #42036 force the whole chart to re-render if coming from the chart
			// editor to prevent the chart from breaking when changing the chart
			// type or calendar range. The chart editor is only available
			// in edit mode. If we're not in edit mode, don't force a re-render
			// or it will break the drilldown animations in maps.
			if (this.canvas.$el.hasClass('editable-canvas')) {
				this.renderChart(this.model.chart.freshRender)
			}
			this.renderTitle()
			// Remove the flag so animations from other rerenders work correctly
			delete this.model.chart.freshRender
			this.applyToAccessoryViews(av => av.renderContent())
		})

		this.listenTo(this.model.data, 'fetching', () =>  {
			this.toggleLoading(true)
		})

		// Note: even though just the label is changing, we need to re-render
		// the entire chart in case it needs to be resized
		this.listenTo(this.model.properties, 'change:label',
			() => this.renderContent())

		_.each(CHART_ACCESSORY_TYPES, type => {
			const accessoryMetadata = getChartAccessoryMetadata(type)
			this.listenTo(this.model.chart,
				`change:${accessoryMetadata.chartProperty}`,
				() => this.toggleChartAccessory(type))
		})

		this.canvas = opt.canvas
		this.firstRender = true
	},

	render: function() {
		WidgetView.prototype.render.apply(this, arguments)

		if (this.$('.content').length === 0) {
			this.$el.append(template())
		}

		// Charts will be loaded by maestro after everything is rendered. So
		// this is ready to display (but not export)
		this.readyForDisplay()
	},

	/**
	 * Renders the actual content of the widget (chart, title, etc) once the
	 * data has been fetched.
	 */
	renderContent () {
		let zoomToIds = getZoomToIds({
			firstRender: this.firstRender,
			state: this.model.initialState,
			chartData: this.model.chart.data
		})

		// Needs to be in the DOM for SVG sizing to work properly
		this.renderChart(false, null, zoomToIds)
		// Must go after rendering the chart because it may need
		// color-legend data. Issue #42477
		this.renderTitle()

		// #40267 If this is a brand-new chart and we are just now showing it, we may need
		// to create the chart table, legend
		// #43545 And this needs to be in the nextTick() so that when using the
		// "duplicate" functionality, it has time to finish adding the data table
		// before this logic runs and potentially creates too many copies
		_.each(CHART_ACCESSORY_TYPES, type => this.toggleChartAccessory(type))

		this.firstRender = false
	},

	/**
	 * Checks whether a chart accessory should appear with the chart, and if
	 * that is the case, creates its widget
	 *
	 * @param accessoryMetadata metadata for the accessory that is being checked
	 */
	toggleChartAccessory: function(type) {
		const accessoryMetadata = getChartAccessoryMetadata(type)
		let wobType = accessoryMetadata.widgetType
		const accessoryEnabled = accessoryMetadata.isEnabled(this.model.chart)
		const accessoryWob = this._findAccessoryWob(wobType)

		if (accessoryEnabled && !accessoryWob) {
			const view = this._createAccessory(accessoryMetadata)
			const newWob = view.model.wob

			if (!this.model.wob.accessoryWobs) this.model.wob.accessoryWobs = []
			this.model.wob.accessoryWobs.push(newWob)

			// #60148 since the chart data is already up to date, we can
			// render the accessory immediately (instead of waiting for
			// maestro to fetch the data)
			view.renderContent()
		}
		if (!accessoryEnabled && accessoryWob) {
			if (!!this.model.wob.accessoryWobs) {
				const wobToRemove = accessoryWob.widget.wob
				_.remove(this.model.wob.accessoryWobs, wob =>
					wob.cid === wobToRemove.cid)
			}
			accessoryWob.widget.wob.destroy()
		}
	},

	resize: function () {
		const wobAttrs = this.model.wob.pick('width', 'height')
		// Only rerender if dimensions changed
		if (!_.isEqual(this.wobAttrs, wobAttrs)) {
			this.renderChart()
		}
	},

	renderTitle: function() {
		let label = this.model.properties.get('label')
		this._colorLegend && this._colorLegend.remove()
		if (label) {
			// #41174 include the period label if enabled
			if (this.model.properties.get('showPeriodLabel')) {
				const period = calendars.periodsById
					[this.model.options.getPeriodId()].label
				label += ` - ${period}`
			}

			this.$('.title-text h3').html(label)

			this.renderColorLegend()
		}

		this.$el.toggleClass('has-title', !!label)
	},

	/**
	 * renders the color legend in the title of the chart widget, for the
	 * color legends that appear separate from the chart,
	 * see #toggleChartAccessory
	 */
	renderColorLegend: function () {
		if (shouldRenderLegend(this.model.chart, CHART_TITLE)) {
			this._colorLegend = new ColorLegend({
				tooltip: true,
				chart: this.model.chart,
				legendType: 'chart-title'
			})

			this.$('.title-text').prepend(this._colorLegend.el)
			this._colorLegend.render()
			this._colorLegend.on('emphasize deemphasize',
				this.emphasize.bind(this))
		}
	},

	emphasize: function (seriesId) {
		this.renderChart(false, seriesId)
	},

	/**
	 * Renders the chart, but AFTER the XHR is complete.
	 */
	renderChart: function (fresh = false, emphasize = null, zoomToIds) {
		if (this.model.chart.get('incompleteReplication')) {
			//#40975 special case for re-replication
			this.renderError({
				message: i18n.get('common.replication.incompleteChart'),
				icon: 'locked'
			})
			return
		}

		if (this.model.chart.hasError()) {
			this.renderError({
				errorResponse: this.model.chart.get('error')
			})
			return
		}

		this.showChart(true)

		this.wobAttrs = this.model.wob.pick('width', 'height')
		let titleHeight = this.model.properties.get('label') ? 38 : 0

		let renderOptions = {
			chartDesigner: this.model.chart,
			chartData: this.model.chart.data,
			container: this.$('.chart-container'),
			fresh,
			dimensions: {
				width: this.wobAttrs.width,
				height: this.wobAttrs.height - titleHeight
			},
			zoomToIds,
			emphasize
		}

		if (this.chartViewObject) {
			renderOptions.chartViewObject = this.chartViewObject
			renderChartDesigner(renderOptions)
		} else {
			this.chartViewObject = renderChartDesigner(renderOptions)

			if (this.chartViewObject) {
				this.drilldownManager = new DrilldownManager({
					chartDesigner: this.model.chart,
					chartViewObject: this.chartViewObject,
					chartWidgetView: this
				})

				// #48807 wait for the chartViewObject itself to be rendered before
				// indicating that it's ready for export
				this.chartViewObject.on('rendered', () =>
					this.readyForDisplayAndExport())
			} else {
				// #52940 if the chart is "empty" (eg no series), then
				// this.chartViewObject may be null. In that case, just say
				// the widget is ready
				this.readyForDisplayAndExport()
			}

			Array.of('drilldown', 'back').forEach(event => {
				this.listenTo(this.drilldownManager, event,
					drilldown => this.onDrilldownOrBack(event, drilldown))
			})

			this.listenTo(this.drilldownManager, 'goto',
				({ serie }) => this.onNav({ serie }))
			this.listenTo(this.drilldownManager, 'rerender',
				() => this.renderChart())
		}

		this.toggleLoading(false)
	},

	renderError: function (errorOpts = {}) {
		this.removeEmptyState()
		this.showChart(false)

		this.emptyState = new WidgetEmptyState(_.extend({}, errorOpts, {
			container: this.container,
			widgetType: 'chart'
		}))

		this.$('.empty-state-container').append(this.emptyState.el)
		this.$('.content').addClass('has-widget-error')
		this.emptyState.render()

		this.toggleLoading(false)
		this.readyForDisplayAndExport()
	},

	showChart(show) {
		this.$('.chart-container').toggleClass('is-not-displayed', !show)
		this.$('.empty-state-container').toggleClass('is-not-displayed', show)
	},

	toggleLoading: function (loading) {
		this.$el.toggleClass('is-loading', loading)
		if (loading) {
			this.$el.append(loadingDots())
		} else {
			this.$('.loading-dots').remove()
		}

		// give loading state to table, legend
		this.applyToAccessoryViews(av => {
			av.toggleLoading(loading)
		})
	},

	_createConfigView: function () {
		return new ChartWidgetConfigMenu({
			model: this.model,
			canvas: this.canvas,
			widgetUpdater: this.widgetUpdater
		})
	},

	_createAccessory: function(accessoryMetadata) {
		const chart = unwrapModel(this.model).chart
		const ModelConstructor = accessoryMetadata.model

		const widgetModel = new ModelConstructor({
			model: this.model.chart,
			properties: _.extend({},{
				type: accessoryMetadata.widgetType,
				chartId: chart.id,
				chartTempId: chart.tempId,
				autoFit: true
			}, _.get(accessoryMetadata, 'defaultAttrs', {}))
		})

		const coords = this.getNextCoords(widgetModel)
		this.canvas.collection.create(coords)

		return this.getAccessoryView(accessoryMetadata.widgetType)
	},

	getNextCoords: function(model) {
		const wobAttrs = this.model.wob.pick('width', 'height', 'top', 'left')
		const isColorLegend = model.get('type') === 'chart-color-legend'
		const top = isColorLegend ?
			wobAttrs.top : wobAttrs.top + wobAttrs.height + WIDGET_MARGIN
		const left = isColorLegend ?
			wobAttrs.left + wobAttrs.width + WIDGET_MARGIN : wobAttrs.left

		// manually set height/width on color legends, depending on if they are
		// horizontal/vertical
		let height = wobAttrs.height
		let width = wobAttrs.width
		if (isColorLegend) {
			const legendType = _.get(model,
				'attributes.model.attributes.colorLegendType')
			if (legendType === 'vertical') {
				width = LEGEND_WIDGET_VERTICAL_WIDTH
			} else {
				height = LEGEND_WIDGET_HORIZONTAL_HEIGHT
			}
		}
		return {
			top,
			left,
			width,
			height,
			widget: model
		}
	},

	_findAccessoryWob: function(type) {
		var chartId = this.model.properties.get('chartId')
		let chartTempId = this.model.chart.get('tempId')
		return this.canvas.collection.chain().filter(function (wob) {
				return wob.widget.get('type') === type
			})
			.find(function (wob) {
				var tableProperties = wob.widget.properties

				if (tableProperties.get('chartTempId') && chartTempId) {
					return tableProperties.get('chartTempId') == chartTempId
				} else if (tableProperties.get('chartId') && chartId) {
					return tableProperties.get('chartId') == chartId
				}
			}).value()
	},

	/**
	 * If corresponding accessory widgets exists, calls the given function,
	 * passing in the table widget's View. If an accessory widget does not
	 * exist, the function is not called on that widget
	 */
	applyToAccessoryViews: function (fn) {
		_.each(CHART_ACCESSORY_TYPES, type => {
			let accessoryView = this.getAccessoryView(type)
			if (accessoryView) {
				fn(accessoryView)
			}
		})
	},

	getAccessoryView: function(type) {
		let accessoryWob = this._findAccessoryWob(type)
		let accessoryWobView = _.find(this.canvas.views, v =>
			v.model === accessoryWob)
		return _.isNil(accessoryWobView) ? null : accessoryWobView.view
	},

	getAccessoryViews: function() {
		const accessoryViews = []
		_.each(CHART_ACCESSORY_TYPES, type => {
			let accessoryView = this.getAccessoryView(type)
			if (accessoryView) {
				accessoryViews.push(accessoryView)
			}
		})
		return accessoryViews
	},

	onDrilldownOrBack: function (event, drilldown) {
		// if this is a leaf node, we want to apply a filter for all the other
		// widgets, but we don't want to change the drilldown of the chart
		// itself. The chart designer is configured so that the zooming in on a
		// leaf node just changes which topojson we are viewing, but it does not
		// change the data/legend associated with the chart
		//
		// i.e. if we are viewing a Texas leaf node, we want the current
		// drilldown to stay as the United States so the legend can maintain
		// values from the surrounding states
		this.model.chart.data.currentDrilldown(drilldown)
		if (drilldown.isZoomed()) {
			// Need to manually re-render the chart; we don't need to fetch new
			// data for this chart, so the fetch-and-rerender process won't run
			this.renderChart()
		}
		this.trigger(event, drilldown)
	},

	onNav: function({ serie }) {
		serieDrilldown({
			chartDesigner: this.model.chart,
			serie,
			widgetModel: this.model
		})
	},

	/**
	 * @override
	 */
	isDrilldownExcluded: function(evtEl) {
		// #45635 'is-clickable' elements on infographic are interactive, so
		// should not use the widget-level drilldown action
		return this.elIsOrIsDescendantOfSelector(evtEl, '.is-clickable')
	},

	datasetId: function () {
		return this.model.datasetId()
	},

	drilldownFieldAndId () {
		return this.model.drilldownFieldAndId()
	},

	removeEmptyState() {
		this.emptyState && this.emptyState.remove()
	},

	/**
	 * Applies the filters from drilling down on another widget (like a dataset
	 * table widget) to the chart's state
	 */
	applyDrilldown (drilldownFilters) {
		let drilldownId = null
		if (drilldownFilters[0]) {
			let drilldownFieldId = drilldownFilters[0].fieldId
			let drilldownField =this.getUnderlyingField(drilldownFieldId)

			let rollupField = this.getUnderlyingField(
				this.model.chart.get('rollupFieldId'))

			// #60442 if we drilled down on a field that isn't the map's rollup
			// field, then we don't want to use that as the drilldownId
			// (we still keep the filters, though)
			if (drilldownField?.id == rollupField?.id) {
				drilldownId = _.keys(drilldownFilters[0]?.rollupIsAnyFilters)[0]
			}
		}

		this.model.chart.data.currentDrilldown(new DrilldownState({
			drilldownFilters,
			drilldownId
		}))
	},

	getUnderlyingField (fieldId) {
		return _(this.model.chart.getAllDatasetIds())
			.map(datasetId => {
				return this.model.chart.data.datasetModel(datasetId)
					?.getUnderlyingField(fieldId)
			})
			.compact()
			.head()
	},

	clearDrilldown () {
		delete this.model.chart.data.clearCurrentDrilldown()
	},

	/**
	 * @override
	 */
	getState: function (options) {
		return {
			chartDesignerId: this.model.chart.id,
			zoomToIds: this.model.chart.data.currentDrilldown()?.zoomToIds()
		}
	},

	remove: function () {
		WidgetView.prototype.remove.apply(this, arguments)
		this._colorLegend && this._colorLegend.remove()
		this.chartViewObject && this.chartViewObject.destroy()
		this.removeEmptyState()
	}
})

/**
 * For the given change event, returns true if the only property that was changed
 * is the given property name. If that property has not changd, or other properties
 * have also changed, returns false.
 */
function singlePropChanged (event, prop) {
	let propsChanged = _.keys(event.changed)
	return propsChanged.length === 1 && propsChanged[0] === prop
}

export default ChartWidgetView

ChartWidgetView.__filename__ = 'dashboards/widgets/chart/chart-widget-view.js'
